import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { LeadSourceNode, StoreSalesInfoDoc } from '@gooduncles/gu-app-schema';
import { Button, Collapse, DatePicker, Spin } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useMemo, useState } from 'react';

import { observeStoreSalesInfos } from 'src/utils/firebase/firebase-short-cut';

import SourceHierarchyList from './SourceHierarchyList';

import classes from './SalesManagementDashboard.module.scss';

const DASHBOARD_COLLAPSE_KEY = 'sales-dashboard-collapsed';
const DASHBOARD_TAB_KEY = 'sales-dashboard-tab';

type TabKey = 'signup' | 'delivery';

interface SalesManagementDashboardProps {
  leadSourceTree: LeadSourceNode[];
}

const SalesManagementDashboard: FC<SalesManagementDashboardProps> = ({ leadSourceTree }) => {
  const [signupDocs, setSignupDocs] = useState<StoreSalesInfoDoc[]>([]);
  const [deliveryDocs, setDeliveryDocs] = useState<StoreSalesInfoDoc[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<dayjs.Dayjs>(dayjs().startOf('month'));
  const [activeKey, setActiveKey] = useState<string[]>(() =>
    localStorage.getItem(DASHBOARD_COLLAPSE_KEY) === 'true' ? [] : ['dashboard'],
  );
  const [activeTab, setActiveTab] = useState<TabKey>(
    () => (localStorage.getItem(DASHBOARD_TAB_KEY) as TabKey) ?? 'signup',
  );

  const monthRange = useMemo(() => {
    const start = selectedMonth.startOf('month').format('YYYY-MM-DD');
    const end = selectedMonth.endOf('month').format('YYYY-MM-DD');
    return { start, end };
  }, [selectedMonth]);

  // storeCreatedAt 기준 구독
  useEffect(() => {
    setIsLoading(true);
    const subscription = observeStoreSalesInfos(
      [
        ['storeCreatedAt', '>=', monthRange.start],
        ['storeCreatedAt', '<=', monthRange.end + 'T23:59:59+0900'],
      ],
      { sortKey: 'storeCreatedAt', orderBy: 'desc' },
    ).subscribe({
      next: (docs) => {
        setSignupDocs(docs);
        setIsLoading(false);
      },
      error: (error) => {
        console.error('Failed to fetch signup docs:', error);
        setIsLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [monthRange]);

  // firstDeliveryDate 기준 구독
  useEffect(() => {
    const subscription = observeStoreSalesInfos(
      [
        ['firstDeliveryDate', '>=', monthRange.start],
        ['firstDeliveryDate', '<=', monthRange.end],
      ],
      { sortKey: 'firstDeliveryDate', orderBy: 'desc' },
    ).subscribe({
      next: (docs) => setDeliveryDocs(docs),
      error: (error) => console.error('Failed to fetch delivery docs:', error),
    });
    return () => subscription.unsubscribe();
  }, [monthRange]);

  const handleTabChange = (key: TabKey) => {
    setActiveTab(key);
    localStorage.setItem(DASHBOARD_TAB_KEY, key);
  };

  const isCurrentMonth = selectedMonth.isSame(dayjs(), 'month');
  const handlePrevMonth = () => setSelectedMonth((prev) => prev.subtract(1, 'month'));
  const handleNextMonth = () => setSelectedMonth((prev) => prev.add(1, 'month'));

  const activeDocs = activeTab === 'signup' ? signupDocs : deliveryDocs;
  const monthLabel = selectedMonth.format('YYYY년 M월');
  const conversionRate = signupDocs.length > 0 ? Math.round((deliveryDocs.length / signupDocs.length) * 1000) / 10 : 0;

  const collapseLabel = (
    <span>
      월간 영업 현황 — {monthLabel} | 첫 배송 <span className={classes.headerDelivery}>{deliveryDocs.length}</span>,
      신규 가입 {signupDocs.length}, 전환율 {conversionRate}%
    </span>
  );

  return (
    <Spin spinning={isLoading}>
      <Collapse
        size='small'
        activeKey={activeKey}
        onChange={(keys) => {
          const next = keys as string[];
          setActiveKey(next);
          localStorage.setItem(DASHBOARD_COLLAPSE_KEY, String(next.length === 0));
        }}
        items={[
          {
            key: 'dashboard',
            label: <strong>{collapseLabel}</strong>,
            children: (
              <div className={classes.dashboardContent}>
                <div className={classes.toolbar}>
                  <div className={classes.monthNav}>
                    <Button type='text' size='small' icon={<LeftOutlined />} onClick={handlePrevMonth} />
                    <DatePicker
                      picker='month'
                      value={selectedMonth}
                      onChange={(date) => date && setSelectedMonth(date.startOf('month'))}
                      allowClear={false}
                      size='small'
                      disabledDate={(current) => current.isAfter(dayjs(), 'month')}
                    />
                    <Button
                      type='text'
                      size='small'
                      icon={<RightOutlined />}
                      onClick={handleNextMonth}
                      disabled={isCurrentMonth}
                    />
                  </div>
                  <div className={classes.tabGroup}>
                    <button
                      className={[
                        classes.tabButton,
                        classes.tabDelivery,
                        activeTab === 'delivery' && classes.tabDeliveryActive,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleTabChange('delivery')}
                    >
                      첫 배송 <strong>{deliveryDocs.length}</strong>
                    </button>
                    <button
                      className={[classes.tabButton, activeTab === 'signup' && classes.tabActive]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleTabChange('signup')}
                    >
                      신규 가입 <strong>{signupDocs.length}</strong>
                    </button>
                  </div>
                </div>
                <SourceHierarchyList docs={activeDocs} leadSourceTree={leadSourceTree} />
              </div>
            ),
          },
        ]}
      />
    </Spin>
  );
};

export default SalesManagementDashboard;
